// RecoveryIndicator — T-016 Part B. Surfaces computeRecoveryScore. Below 40
// the indicator shows a deload warning in --color-blood.

import type { JSX } from "react";
import { useI18n } from "../../i18n";

export interface RecoveryIndicatorProps {
  /** 0-100 score from computeRecoveryScore. */
  score: number;
  /** Optional explicit "today" anchor (defaults to today's date). */
  today?: string;
}

export function RecoveryIndicator({ score, today }: RecoveryIndicatorProps): JSX.Element {
  const { t } = useI18n();
  const safe = Math.max(0, Math.min(100, Math.round(score)));
  const warn = safe < 40;
  const color = warn ? "var(--color-blood)" : "var(--color-lime)";
  return (
    <div
      data-testid="recovery-indicator"
      data-warn={warn}
      aria-live="polite"
      className="flex flex-col gap-2 border border-[var(--color-line)] bg-[var(--color-slab)] p-4"
      style={{ borderRadius: "var(--radius-card)", minWidth: 200 }}
    >
      <p className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
        {t("analytics.recoveryLabel")}
      </p>
      <div className="flex items-baseline gap-2">
        <span
          className="font-numeric tabular"
          style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, color }}
        >
          {safe}
        </span>
        <span className="font-sans text-sm text-[var(--color-ink-mute)]">/ 100</span>
      </div>
      <div
        className="h-2 w-full"
        style={{ backgroundColor: "var(--color-line)", borderRadius: "var(--radius-sharp)" }}
        role="progressbar"
        aria-valuenow={safe}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          style={{
            width: `${safe}%`,
            height: "100%",
            backgroundColor: color,
            transition: "width 200ms cubic-bezier(0.2, 0, 0, 1)",
          }}
        />
      </div>
      {warn && (
        <p
          className="font-sans text-sm"
          style={{ color: "var(--color-blood)" }}
          data-testid="recovery-deload"
        >
          {t("analytics.recoveryDeload")}
        </p>
      )}
      {today && (
        <p className="sr-only">{today}</p>
      )}
    </div>
  );
}
